'use client';

import { Slot } from 'radix-ui';
import { useEffect, useRef, type ReactNode } from 'react';

/*
 * The menu both panels draw (docs/11 §8): the logo centred on top, the groups of
 * entries, and what stays pinned under them. The pieces take links and words
 * from the app — the router and the dictionary are the app's — so the seller
 * panel and the platform panel read as one product whatever they list.
 */

/** The column on the left of a wide screen; below `lg` the app shows the same pieces in a drawer. */
export function Sidebar({ children }: { children: ReactNode }) {
    return (
        <aside data-slot="sidebar" className="sticky top-0 hidden h-dvh flex-col border-r border-border bg-card px-3 pb-3 lg:flex">
            {children}
        </aside>
    );
}

/**
 * The brand row, with the logo centred in it: as tall as the top bar, so the two
 * line up, and taller only when a panel sets a tag under its logo.
 */
export function SidebarBrand({ children }: { children: ReactNode }) {
    return (
        <div data-slot="sidebar-brand" className="flex min-h-14 shrink-0 flex-col items-center justify-center gap-1 p-2">
            {children}
        </div>
    );
}

/**
 * The entries. On a window too short for all of them it scrolls, and the entry
 * of the page on screen is never the one left out of sight — after a link inside
 * a page as much as after a click in the menu, hence `path`.
 */
export function SidebarNav({ label, path, children }: { label: string; path: string; children: ReactNode }) {
    const menu = useRef<HTMLElement>(null);

    useEffect(() => {
        menu.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
    }, [path]);

    return (
        <nav ref={menu} aria-label={label} data-slot="sidebar-nav" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto py-1">
            {children}
        </nav>
    );
}

/** The entries of one job under a quiet label — a label, not a control: the group is always open. */
export function SidebarGroup({ title, children }: { title?: ReactNode; children: ReactNode }) {
    return (
        <div data-slot="sidebar-group" className="flex flex-col gap-1">
            {title && <span className="px-3 text-11 font-medium tracking-label text-muted-foreground uppercase">{title}</span>}
            {children}
        </div>
    );
}

/**
 * One entry, drawn onto its one child — a router link, or the button that signs
 * out — with the icon before the child's label and a count after it. 28px, so
 * the whole menu fits a laptop screen; a touch screen gets its 44px target back.
 * `aria-current="page"` on the child marks the page on screen: the 3px rail
 * lives in the border, transparent until then, so nothing shifts.
 */
export function SidebarItem({ icon, badge, children }: { icon: ReactNode; badge?: ReactNode; children: ReactNode }) {
    return (
        <Slot.Root
            data-slot="sidebar-item"
            className="group flex min-h-7 w-full items-center gap-3 rounded-control border-l-3 border-transparent px-3 text-14 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-55 aria-[current=page]:border-primary aria-[current=page]:bg-primary-subtle aria-[current=page]:font-medium aria-[current=page]:text-foreground pointer-coarse:min-h-11 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:opacity-80 [&_svg]:group-hover:opacity-100 [&_svg]:group-aria-[current=page]:text-primary [&_svg]:group-aria-[current=page]:opacity-100"
        >
            {icon}
            <Slot.Slottable>{children}</Slot.Slottable>
            {badge && <span className="ms-auto">{badge}</span>}
        </Slot.Root>
    );
}

/** What stays under the menu, never scrolled away with it: the account row, signing out. */
export function SidebarFooter({ children }: { children: ReactNode }) {
    return (
        <div data-slot="sidebar-footer" className="flex shrink-0 border-t border-border pt-3">
            {children}
        </div>
    );
}
